import React, { useEffect, useMemo, useState } from 'react';
import EntryAssessmentPanel from './EntryAssessmentPanel';
import { useNavigate } from 'react-router-dom';
import {
  getFacilitators,
  getStudentsOverview,
  getMonitorOutcomes,
  getOutcomesTrend,
  getActivityBySchool,
  getActivityByHour,
  getMonitorProjectsShared,
  FacilitatorUsage as Facilitator,
  StudentsOverview,
  MonitorOutcomes,
  OutcomesTrend,
  ActivityBySchool,
  ActivityByHour,
  ProjectsShared,
} from '../../services/api';
import MonitorLayout from './MonitorLayout';
import { InfoDot, MetricTile as Tile } from './MetricTile';
import { Period, periodRange, trendDaysFor, periodReady, describePeriod } from './period';
import PeriodPicker from './PeriodPicker';

const DAY = 24 * 60 * 60 * 1000;
const daysSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / DAY);
};
const relLabel = (iso?: string | null): string => {
  const d = daysSince(iso);
  if (d == null) return 'Never';
  if (d <= 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return new Date(iso as string).toLocaleDateString();
};

// --- CSV export (M&E pull the facilitator activity into Excel) --------------
// Kept in step with the tiles on the dashboard. The 2026-08-26 call dropped chat
// COUNTS (time replaced them), PDF-upload tracking and the whole visit schedule
// (planned days / missed visits were "not trackable operationally"), so they are
// gone from the export too — M&E should not find columns here that the dashboard
// no longer shows.
const CSV_HEADERS = [
  'Name', 'Username', 'School', 'Subjects', 'Classes', 'Created', 'Last login',
  'Last active', 'Sessions conducted', 'Students enrolled', 'Attendance %',
  'Coach minutes', 'Active days', 'Avg min/active day',
  'Lesson plans', 'Topics ticked', 'Reinforcements', 'Assessments',
  'Checked in today', 'Top topics',
];
const csvCell = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const facRow = (f: Facilitator): string =>
  [
    f.name || '', f.username, f.school, (f.subjects || []).join('; '),
    (f.classes || []).map((c) => `${c.grade}${c.section ? `-${c.section}` : ''}`).join('; '),
    f.created_at || '', f.last_login_at || '', f.last_active || '',
    f.sessions_conducted, f.students_enrolled, f.attendance_pct ?? '',
    f.coach_minutes, f.active_days,
    f.active_days ? Math.round(f.coach_minutes / f.active_days) : 0,
    f.lesson_plans, f.topics_ticked, f.reinforcements, f.assessments,
    f.checked_in_today ? 'yes' : 'no',
    (f.top_topics || []).join('; '),
  ].map(csvCell).join(',');
const exportFacilitatorsCsv = (rows: Facilitator[], periodLabel: string) => {
  const csv = [CSV_HEADERS.map(csvCell).join(','), ...rows.map(facRow)].join('\n');
  // BOM so Excel reads UTF-8 (facilitator/school names) correctly.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `facilitators-${periodLabel}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// Says in words which buckets the averages were taken over. "This week" is
// Monday->today by definition, so it can only ever be a part week; the month and
// all-time views average WHOLE weeks (see _live_weeks in the backend). Athira's
// team should be able to read the basis off the screen instead of asking.
const periodBasis = (o: MonitorOutcomes): string => {
  if (!o.weeks_averaged) return 'no activity yet in this period';
  const wk = o.weeks_partial
    ? `part week so far (${o.weeks_averaged})`
    : `${o.weeks_averaged} whole week${o.weeks_averaged === 1 ? '' : 's'}`;
  const dy = o.days_partial
    ? 'today so far'
    : `${o.days_averaged} school day${o.days_averaged === 1 ? '' : 's'}`;
  return `students averaged over ${wk} · facilitators over ${dy}`;
};

const fmtMins = (m: number): string => {
  if (!m) return '0m';
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return h ? `${h}h ${mm}m` : `${mm}m`;
};

// "count · pct" without the two contradicting each other. A whole-number count
// can hide a real fraction — 0.4 facilitators/day rounds to 0 but is still 3% —
// so when the count rounds to 0 while the percentage is not zero, we show "<1"
// instead of a misleading "0". (Athira flagged the "0 · 3%" tile.)
const countDotPct = (count: number, pct: number | null): string => {
  const c = count === 0 && pct != null && pct > 0 ? '<1' : String(count);
  return pct == null ? c : `${c} · ${pct}%`;
};

// The rings already show the TRUE percentage (an average of totals). We
// deliberately do not print "4/7" beside it: 4 and 7 are each rounded weekly
// averages, so "4/7" reads as 57% while the real ratio is 52% — the exact
// "the two numbers don't agree" contradiction Athira caught. We show only the
// base (how many students were active in a typical week) as scale, so nothing
// invites a division that fights the ring's own percentage.
const perWeekBase = (activeStudents: number): string =>
  `of ~${activeStudents} student${activeStudents === 1 ? '' : 's'} active/week`;

// Metrics the facilitator bar chart can rank by. `get` rather than a field name,
// because coach time now ranks on the DAILY AVERAGE: a period total rewards the
// facilitator who has simply been around longest, which is not what Athira asked
// to see (2026-08-26 — "the average time the facilitator is spending on the app").
type MetricKey = 'avg_min_per_day' | 'coach_minutes' | 'active_days' | 'lesson_plans' | 'topics_ticked';
const avgMinPerDay = (f: Facilitator): number =>
  f.active_days ? Math.round(f.coach_minutes / f.active_days) : 0;
const METRICS: {
  key: MetricKey; label: string; fmt: (n: number) => string; get: (f: Facilitator) => number;
}[] = [
  { key: 'avg_min_per_day', label: 'Coach time / day', fmt: fmtMins, get: avgMinPerDay },
  { key: 'coach_minutes', label: 'Coach time (total)', fmt: fmtMins, get: (f) => f.coach_minutes },
  { key: 'active_days', label: 'Active days', fmt: (n) => String(n), get: (f) => f.active_days },
  { key: 'lesson_plans', label: 'Lesson plans', fmt: (n) => String(n), get: (f) => f.lesson_plans },
  { key: 'topics_ticked', label: 'Topics', fmt: (n) => String(n), get: (f) => f.topics_ticked },
];
const metricOf = (key: MetricKey) => METRICS.find((m) => m.key === key)!;

const MonitorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Facilitator[]>([]);
  const [overview, setOverview] = useState<StudentsOverview | null>(null);
  const [outcomes, setOutcomes] = useState<MonitorOutcomes | null>(null);
  const [outcomesTrend, setOutcomesTrend] = useState<OutcomesTrend | null>(null);
  const [bySchool, setBySchool] = useState<ActivityBySchool | null>(null);
  const [byHour, setByHour] = useState<ActivityByHour | null>(null);
  const [projects, setProjects] = useState<ProjectsShared | null>(null);
  const [period, setPeriod] = useState<Period>('all');
  // Only meaningful when period === 'custom' — the calendar range (task #19).
  const [customSince, setCustomSince] = useState<string | undefined>(undefined);
  const [customUntil, setCustomUntil] = useState<string | undefined>(undefined);
  const [chartMetric, setChartMetric] = useState<MetricKey>('avg_min_per_day');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onAuthError = (msg: string) => {
    if (/token|unauth/i.test(msg)) {
      localStorage.removeItem('monitorToken');
      navigate('/monitor/login', { replace: true });
    }
  };

  // The period selector is GLOBAL — every activity/outcome metric re-fetches
  // for the selected window when it changes.
  useEffect(() => {
    const token = localStorage.getItem('monitorToken');
    if (!token) {
      navigate('/monitor/login', { replace: true });
      return;
    }
    if (!periodReady(period, { since: customSince, until: customUntil })) {
      // "Custom range" with no date picked yet — wait for the user.
      setLoading(false);
      return;
    }
    const w = periodRange(period, { since: customSince, until: customUntil });
    const trendDays = trendDaysFor(period);
    setRefreshing(true);
    Promise.all([
      getFacilitators(token, w).then(setRows),
      getStudentsOverview(token, w).then(setOverview),
      getMonitorOutcomes(token, w).then(setOutcomes),
      getOutcomesTrend(token, trendDays, w).then(setOutcomesTrend),
      getActivityBySchool(token, w).then(setBySchool),
      getActivityByHour(token, w).then(setByHour),
      getMonitorProjectsShared(token, w).then(setProjects),
    ])
      .then(() => setError(null))
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        onAuthError(msg);
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customSince, customUntil, navigate]);

  const stats = useMemo(() => {
    const sum = (f: (x: Facilitator) => number) => rows.reduce((a, x) => a + f(x), 0);
    // NOTE "% facilitators under 30 min/day" is no longer worked out here. It was
    // one window-wide ratio, which is the average-of-averages problem Athira
    // walked us through on 2026-08-19. It is now bucketed per school day in the
    // backend and arrives as outcomes.under30_pct.
    return {
      // Count EVERY school a facilitator serves — their own plus the schools of
      // their classes — not just the primary `school` column. A multi-school
      // facilitator (3 schools, one Teacher row) previously collapsed to 1 here,
      // so 14 facilitators covering 21 schools showed as "14 onboarded schools".
      schools: new Set(
        rows.flatMap((f) => [f.school, ...(f.classes || []).map((c) => c.school)]).filter(Boolean)
      ).size,
      total: rows.length,
      // NOTE there is deliberately no "active facilitators" count computed here.
      // It used to live in this file with its own rule while /outcomes had a
      // second, different one (which also counted bare logins) — the two tiles
      // then disagreed, and nobody could say why (Athira, 2026-08-19). The
      // single definition now lives in the backend: outcomes.facilitators_daily.
      missedVisits: sum((f) => f.missed_visits),
      coachMinutes: sum((f) => f.coach_minutes),
      topics: sum((f) => f.topics_ticked),
      lessonPlans: sum((f) => f.lesson_plans),
    };
  }, [rows]);

  // Bar chart: facilitators ranked by the selected metric (top 12 with activity).
  const chartRows = useMemo(
    () =>
      [...rows]
        .filter((f) => metricOf(chartMetric).get(f) > 0)
        .sort((a, b) => metricOf(chartMetric).get(b) - metricOf(chartMetric).get(a))
        .slice(0, 12),
    [rows, chartMetric]
  );
  const chartMax = chartRows.length ? metricOf(chartMetric).get(chartRows[0]) : 1;

  return (
    <MonitorLayout>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Program monitoring</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            How facilitators and students are using Curiosity, across all schools
          </p>
        </div>
        <div className="flex flex-none items-center gap-2">
          <PeriodPicker
            value={{ period, since: customSince, until: customUntil }}
            onChange={(v) => { setPeriod(v.period); setCustomSince(v.since); setCustomUntil(v.until); }}
          />
          {!loading && !error && rows.length > 0 && (
            <button
              type="button"
              onClick={() => exportFacilitatorsCsv(rows, period)}
              className="rounded-lg border border-[#E9E6F4] bg-white px-3 py-2 text-xs font-semibold text-violet-700 shadow-sm hover:bg-violet-50"
            >
              ↓ Export CSV
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : error ? (
        <p className="py-10 text-center text-sm text-rose-600">{error}</p>
      ) : (
        <>
          {/* No-activity banner — a single day (or any range) with no coach use
              used to show a confusing wall of zeros. Say it plainly instead. The
              roster tiles below still show, because they are true regardless. */}
          {outcomes && outcomes.weeks_averaged === 0 && outcomes.days_averaged === 0 && (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              No coach activity in {describePeriod(period, { since: customSince, until: customUntil })}.
              The usage numbers below are all zero for this range — try a wider range or a different date.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
            <Tile
              value={String(stats.schools)}
              label="Onboarded schools"
              hint="How many schools have been set up on Curiosity Coach. Being set up does not mean they are using it — the usage tiles below show that."
              tint="bg-purple-100 text-purple-700"
            />
            <Tile
              value={String(stats.total)}
              label="Onboarded facilitators"
              hint="How many facilitators have an account. Having an account does not mean they are using it — see 'Facilitators using it daily'."
              tint="bg-violet-100 text-violet-700"
            />
            <Tile
              value={String(overview?.total_students ?? 0)}
              label="Onboarded students"
              hint="Every student who has an account, across all schools. This is the full roster (all onboarded), not how many are using the app — the usage tiles show that."
              tint="bg-blue-100 text-blue-700"
            />
            <Tile
              value={countDotPct(outcomes?.facilitators_daily ?? 0, outcomes?.facilitators_daily_pct ?? null)}
              label="Facilitators using it daily"
              hint={`On a normal school day, this many facilitators actually DID something on the app — asked the coach a question, made a lesson plan, or ticked off a module. Just opening the app and closing it does not count. ${
                outcomes?.facilitators_daily_pct != null
                  ? `That is ${outcomes.facilitators_daily_pct}% of all ${outcomes.facilitators_total} onboarded facilitators. `
                  : ''
              }${
                outcomes?.days_partial
                  ? 'This period only covers today so far, so treat it as a part-day reading.'
                  : `We looked at each of the ${outcomes?.days_averaged ?? 0} finished school day(s) in this period and took the average. Today is not counted while it is still going on.`
              }`}
              tint="bg-emerald-100 text-emerald-700"
            />
            <Tile
              value={countDotPct(outcomes?.active_students ?? 0, outcomes?.active_students_pct ?? null)}
              label="Students using it weekly"
              hint={`In a normal week, this many students chatted with the coach at least once. ${
                outcomes?.active_students_pct != null
                  ? `That is ${outcomes.active_students_pct}% of the ${outcomes.students_total} onboarded students — the share is what stays meaningful as the roster grows past 3,500. `
                  : ''
              }${
                outcomes?.weeks_partial
                  ? 'This period is shorter than a full week, so this is the part week so far.'
                  : `We counted the students week by week across the ${outcomes?.weeks_averaged ?? 0} full week(s) in this period and took the average. Half-finished weeks at the start or end are left out, because a 2-day week would unfairly drag the number down.`
              }`}
              tint="bg-green-100 text-green-700"
            />
            <Tile
              value={
                outcomes?.facilitator_minutes_per_day == null
                  ? '—'
                  : fmtMins(outcomes.facilitator_minutes_per_day)
              }
              label="Time a facilitator spends, per active day"
              hint={
                outcomes?.facilitator_minutes_per_day == null
                  ? 'No facilitator used the coach in this period, so there is no time to average.'
                  : `On a day a facilitator actually uses the coach, this is how long they spend on it — their own chats PLUS time in the Planner. ${fmtMins(outcomes.facilitator_minutes_total)} in total across ${outcomes.facilitator_active_days} facilitator-day(s), divided once, so every facilitator-day weighs the same and the figure does not drift just because more facilitators are onboarded. Gaps over 5 minutes count as walking away. Planner time is recorded from 27 Aug 2026 onwards; planning before that was never timestamped.`
              }
              tint="bg-sky-100 text-sky-700"
            />
            <Tile
              value={outcomes?.under30_pct == null ? '—' : `${outcomes.under30_pct}%`}
              label={
                outcomes
                  ? `Facilitators <30 min/day · ${outcomes.under30_facilitators} of ${outcomes.under30_base}`
                  : 'Facilitators <30 min/day'
              }
              hint={
                !outcomes
                  ? 'Facilitators averaging less than 30 minutes of coach usage per day during the selected period.'
                  : `Facilitators averaging less than 30 minutes of coach usage per day during the selected period. This INCLUDES facilitators with no usage — someone who never opens the coach must not disappear from the metric. For each facilitator we take all their coach time in the period (chats plus Planner) and divide it by the ${outcomes.under30_days_in_range} day(s) in the period, not by the days they happened to be active: 80 minutes spread across a week is 11 min/day, not 40. ${outcomes.under30_facilitators} of ${outcomes.under30_base} onboarded facilitators are under the target. It moves with the period filter, so "this week" and "this month" each get their own divisor. Whether facilitators are using the coach at all is the "Facilitators using it daily" tile.`
              }
              tint="bg-emerald-100 text-emerald-700"
            />
            <Tile
              value={
                outcomes?.students_every_week_pct == null
                  ? '—'
                  : countDotPct(outcomes.students_every_week, outcomes.students_every_week_pct)
              }
              label="Same students returning every week"
              hint={
                outcomes?.students_every_week_pct == null
                  ? 'This needs at least two full weeks in the period before it means anything — in a single week, everyone who used it "used it every week".'
                  : `${outcomes.students_any_week} different students used the coach at some point in this period. Of those, ${outcomes.students_every_week} (${outcomes.students_every_week_pct}%) came back in EVERY single week — the same children returning again and again, not a different set each week${
                      outcomes.students_every_week_roster_pct != null
                        ? ` (that is ${outcomes.students_every_week_roster_pct}% of the whole onboarded roster)`
                        : ''
                    }.`
              }
              tint="bg-teal-100 text-teal-700"
            />
            <Tile
              value={String(stats.lessonPlans)}
              label="Lesson plans created"
              hint="Lesson plans facilitators BUILT with the Planner in this period. One per module they asked activities for, counted once per module per day, as you defined it on the 26 Aug call — so a facilitator working through the term climbs towards their ~66. A plan counts the moment it is built, even if they never save or push it; planning typed into the general chat is not counted."
              tint="bg-violet-100 text-violet-700"
            />
            <Tile
              value={
                outcomes?.student_minutes_per_day == null
                  ? '—'
                  : fmtMins(outcomes.student_minutes_per_day)
              }
              label="Time a student spends, per active day"
              hint={
                outcomes?.student_minutes_per_day == null
                  ? 'No student used the coach in this period, so there is no time to average.'
                  : `On a day a child actually uses the coach, this is how long they spend on it. ${fmtMins(outcomes.student_minutes_total)} in total across ${outcomes.student_active_days} student-day(s) in this period, divided once — every child-day weighs the same, so one long afternoon cannot carry the number. Time is measured between messages and a gap longer than 5 minutes is treated as "walked away", so idle chats are not billed as time. This replaced the old "student chats" count (call 2026-08-26 — a count of chats told nobody anything).`
              }
              tint="bg-cyan-100 text-cyan-700"
            />
            <Tile
              value={String(stats.topics)}
              label="Topics completed"
              hint="Curriculum topics facilitators ticked off as done with their class in this period."
              tint="bg-indigo-100 text-indigo-700"
            />
          </div>

          {/* Learning outcomes (M&E, CC-dashboard source) — program-level. */}
          {outcomes && (
            <div className="mt-5">
              <h2 className="mb-2 text-sm font-bold text-slate-900">
                Learning outcomes
                <span className="ml-2 text-[11px] font-medium text-slate-400">
                  {period === 'custom' && customSince && customSince === customUntil
                    ? `figures for ${describePeriod(period, { since: customSince, until: customUntil })} (a single day, not a weekly average)`
                    : periodBasis(outcomes)}{' '}
                  · hover any ring for how it is worked out
                </span>
              </h2>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                {/* The module pair (Athira 2026-08-19). These are exact
                    complements, so they always add to 100% — the old
                    "exploring independently" vs "using STEM curriculum" pair
                    overlapped and could not be read against each other. */}
                <OutcomeRing
                  pct={outcomes.inside_module_pct}
                  color="#0891B2"
                  who="Students"
                  title="Asking about the module"
                  sub={perWeekBase(outcomes.active_students)}
                  hint="Students who asked about the STEM modules — a topic their facilitator sent them, or anything from the STEM curriculum. We read the TOPICS each chat actually covered, so a chat that starts on a module counts here even if the child later moves on to something of their own (which also counts under 'Asked about something else'). Each student counts once per week, and we average the weeks."
                />
                <OutcomeRing
                  pct={outcomes.outside_module_pct}
                  color="#7C3AED"
                  who="Students"
                  title="Asked about something else"
                  sub={perWeekBase(outcomes.active_students)}
                  hint="Students who asked about something other than the STEM modules — their own curiosity, not the syllabus. We read the TOPICS each chat actually covered, so a child who opens a module and then wanders off into a question of their own is counted here AND in 'Asking about the module' — the two can add up to more than 100%. A topic counts as module work when it shares real words with the loaded curriculum. Chats the analysis has not reached yet fall back to the chat's overall theme."
                />
                <OutcomeRing
                  pct={outcomes.exploratory_pct}
                  color="#C026D3"
                  who="Students"
                  title="Asking curious questions"
                  sub={perWeekBase(outcomes.active_students)}
                  hint="Students who asked at least one real question to understand the topic — not only why/how/what-if, but any genuine question ('what is a black hole?', 'does sound travel in space?', 'is it true that…', 'I wonder…'), in English, Hindi (क्यों / कैसे / क्या / कब) or Hinglish (kyu / kaise / kya), including 'I don't understand why…'. Greetings ('how are you' / 'कैसे हो'), asking for help with a task ('how do I do this'), fishing for the answer ('what's the answer', 'is this correct'), and questions about the bot do NOT count. Counted per student, not per message — one chatty child cannot push it up alone."
                />
                <OutcomeRing
                  pct={outcomes.after_school_pct}
                  color="#7C3AED"
                  who="Students"
                  title="Engaging after school"
                  sub={perWeekBase(outcomes.active_students)}
                  hint="Students who used the coach outside school time — in the evening, early morning, or on the weekend. School time is set to Mon–Fri, 9am to 4pm for now; VU still has to confirm the exact hours."
                />
                <OutcomeRing
                  pct={outcomes.take_home_pct}
                  color="#DB2777"
                  who="Students"
                  title="Getting take-home prompts"
                  sub={`${outcomes.take_home_prompts} chats · ${perWeekBase(outcomes.active_students)}`}
                  hint="Students whose chat ended with the coach giving them something to think about at home — an unfinished mystery or a small challenge to try."
                />
                <OutcomeRing
                  pct={outcomes.facilitators_daily_pct}
                  color="#059669"
                  who="Facilitators"
                  title="Facilitators using CC daily"
                  sub={`${
                    outcomes.facilitators_daily === 0 && (outcomes.facilitators_daily_pct ?? 0) > 0
                      ? '<1'
                      : outcomes.facilitators_daily
                  } of ${outcomes.facilitators_total} on an average school day`}
                  hint="Out of all facilitators with an account, the share who actually did something on the app on a normal school day — asked the coach, made a lesson plan, or ticked a module. Just logging in does not count. This is the 'Facilitators using it daily' tile shown as a percentage."
                />
                <OutcomeRing
                  pct={outcomes.module_coverage_pct}
                  color="#D97706"
                  who="Facilitators"
                  title="STEM modules delivered via CC"
                  sub={
                    outcomes.modules_total
                      ? `${outcomes.modules_covered}/${outcomes.modules_total} modules covered`
                      : 'no module list loaded'
                  }
                  hint={`How much of the pilot's module list has been taught through CC. The denominator is the ${outcomes.modules_total} yellow-marked modules on VU's curriculum sheet — the ones due by March, as you set it on the 26 Aug call — not the ${outcomes.modules_loaded} modules loaded in the curriculum, which covers more than this pilot. Each of the 15 carries the grades the sheet tags on it, and a module counts only when it is taught to one of ITS OWN grades: two of the 15 are the same material split by grade (Grade 6 paper circuits vs the Grade 7 switches capstone), and teaching a Grade 8 module to a Grade 6 class is not that module delivered. It counts when a facilitator builds a CC lesson plan on it or ticks one of its topics as covered${
                    outcomes.coverage_until ? ` (counting up to ${outcomes.coverage_until})` : ''
                  }. The old version divided app logins by planned visit days — that visit-schedule denominator is gone.${
                    outcomes.modules_untracked
                      ? ` IMPORTANT: ${outcomes.modules_untracked} of the ${outcomes.modules_total} have no content in CC at all — ${outcomes.modules_untracked_names.join('; ')}. Nothing was ever delivered for them, so a facilitator cannot build a plan or tick a topic, and they can never count here until VU send the material. The realistic ceiling today is ${outcomes.modules_total - outcomes.modules_untracked}/${outcomes.modules_total}.`
                      : ''
                  } ${
                    outcomes.modules_taught_any
                      ? `Separately, ${outcomes.modules_taught_any} module(s) were taught through CC in this period counting everything, pilot or not — so a low ring is not by itself facilitators being idle.`
                      : ''
                  } ${
                    outcomes.modules_covered_names.length
                      ? `Delivered so far: ${outcomes.modules_covered_names.join(', ')}.`
                      : ''
                  }`}
                />
              </div>
              {outcomesTrend && outcomesTrend.points.length > 0 && (
                <div className="mt-3 rounded-2xl border border-[#E9E6F4] bg-white p-5">
                  <h3 className="mb-3 text-sm font-bold text-slate-900">
                    How things are changing
                    <span className="ml-1 text-[11px] font-medium text-slate-400">
                      pick one thing to look at · {outcomesTrend.points.length} days · move your cursor over the chart to see any day
                    </span>
                  </h3>
                  <OutcomeTrendChart points={outcomesTrend.points} />
                </div>
              )}
            </div>
          )}

          <EntryAssessmentPanel />

          {/* Hackathons (#169) — renamed from "Student projects" per call 2026-08-26.
              Window count + fixed vacation count + proxy. */}
          {projects && (
            <div className="mt-5 rounded-2xl border border-[#E9E6F4] bg-white p-5">
              <h2 className="mb-3 text-sm font-bold text-slate-900">
                Hackathons{' '}
                <span className="ml-1 text-[11px] font-medium text-slate-400">projects students made &amp; shared</span>
              </h2>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                <Tile
                  value={String(projects.window_count)}
                  label={`Shared · ${describePeriod(period, { since: customSince, until: customUntil })}`}
                  hint="Projects students uploaded and shared in this period."
                  tint="bg-violet-100 text-violet-700"
                />
                <Tile
                  value={String(projects.vacation_count)}
                  label={`Shared during vacation (${projects.vacation_label})`}
                  hint="Projects shared during the school vacation window. This one always covers the vacation dates shown, whatever period is picked above."
                  tint="bg-amber-100 text-amber-700"
                />
                <Tile
                  value={String(projects.chat_mentions)}
                  label="Mentioned a project in chat (proxy)"
                  hint="Chats where a student talked about a project they made ('my project', 'I built...'). A rough signal, not an exact count — they may not have uploaded it."
                  tint="bg-sky-100 text-sky-700"
                />
              </div>
              {projects.recent.length > 0 && (
                <div className="mt-4 divide-y divide-[#F1EFFA]">
                  {projects.recent.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 py-2.5">
                      {p.image_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_BASE_URL || ''}${p.image_url}`}
                          alt={p.title}
                          className="h-10 w-10 flex-none rounded-lg border border-slate-100 object-cover"
                        />
                      ) : (
                        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-violet-50 text-violet-400">◆</span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-800">{p.title}</p>
                        <p className="text-xs text-slate-400">
                          {p.student_name || 'Student'} · {p.school} · Grade {p.grade}{p.section ? `-${p.section}` : ''}
                        </p>
                      </div>
                      <span className="flex-none text-xs text-slate-400">{relLabel(p.shared_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* When students use the coach — hourly rhythm, after-school highlighted.
              Always rendered (with an empty state) so the section never silently
              disappears — Athira 2026-08-25 couldn't find it because it used to
              vanish whenever the selected period had no messages. */}
          {byHour && (
            <div className="mt-5 rounded-2xl border border-[#E9E6F4] bg-white p-5">
              <h2 className="mb-1 text-sm font-bold text-slate-900">
                When students use the coach{' '}
                <span className="ml-1 text-[11px] font-medium text-slate-400">
                  student messages by hour · IST · {describePeriod(period, { since: customSince, until: customUntil })}
                </span>
              </h2>
              <p className="mb-3 text-xs text-slate-400">
                School hours = Mon–Fri {SCHOOL_HOURS_LABEL}; everything else (evenings, early mornings, weekends) counts as after school. VU still to confirm the exact bell times and whether Saturday is a school day.
              </p>
              {byHour.hours.some((h) => h.school + h.after_school > 0) ? (
                <HourlyChart hours={byHour.hours} afterPct={byHour.after_school_pct} />
              ) : (
                <p className="py-8 text-center text-xs text-slate-400">
                  No student messages in {describePeriod(period, { since: customSince, until: customUntil })} yet — this fills in once students start chatting.
                </p>
              )}
            </div>
          )}

          {/* Composition: which schools drive the activity (part-to-whole donut). */}
          {bySchool && bySchool.schools.length > 0 && (
            <div className="mt-5 rounded-2xl border border-[#E9E6F4] bg-white p-5">
              <h2 className="mb-4 text-sm font-bold text-slate-900">
                Student activity by school{' '}
                <span className="ml-1 text-[11px] font-medium text-slate-400">
                  active students · time per active day · {describePeriod(period, { since: customSince, until: customUntil })}
                </span>
              </h2>
              <SchoolDonut schools={bySchool.schools} />
            </div>
          )}

          {/* Graphical: facilitators ranked by a selectable metric. */}
          <div className="mt-5 rounded-2xl border border-[#E9E6F4] bg-white p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-slate-900">Facilitators by activity</h2>
              <div className="flex flex-wrap gap-1">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setChartMetric(m.key)}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${
                      chartMetric === m.key
                        ? 'bg-violet-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {chartRows.length === 0 ? (
              <p className="text-sm text-slate-400">No facilitator activity in this period yet.</p>
            ) : (
              <div className="space-y-2">
                {chartRows.map((f) => {
                  const v = metricOf(chartMetric).get(f);
                  const pct = Math.max(3, Math.round((v / chartMax) * 100));
                  const fmt = metricOf(chartMetric).fmt;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => navigate(`/monitor/facilitators/${f.id}`)}
                      className="group flex w-full items-center gap-3 text-left"
                    >
                      <span className="w-28 flex-none truncate text-xs font-semibold text-slate-700 sm:w-36">
                        {f.name || f.username}
                      </span>
                      <span className="relative h-5 flex-1 overflow-hidden rounded bg-slate-100">
                        <span
                          className="absolute inset-y-0 left-0 rounded bg-violet-500 transition-all group-hover:bg-violet-600"
                          style={{ width: `${pct}%` }}
                        />
                      </span>
                      <span className="w-16 flex-none text-right text-xs font-bold tabular-nums text-slate-800">
                        {fmt(v)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 rounded-2xl border border-[#E9E6F4] bg-white p-5">
            <h2 className="mb-3 text-sm font-bold text-slate-900">
              Most active students{' '}
              <span className="ml-1 text-[11px] font-medium text-slate-400">
                by time on the coach · {describePeriod(period, { since: customSince, until: customUntil })}
              </span>
            </h2>
            {!overview || overview.most_active_students.length === 0 ? (
              <p className="text-sm text-slate-400">No student activity yet.</p>
            ) : (
              <div className="divide-y divide-[#F1EFFA]">
                {overview.most_active_students.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-800">{s.name}</p>
                      <p className="text-xs text-slate-400">
                        {s.school} · Grade {s.grade}{s.section ? `-${s.section}` : ''} · active {relLabel(s.last_active)}
                      </p>
                    </div>
                    <span
                      className="flex-none cursor-help text-right text-xs"
                      title={`${fmtMins(s.minutes_total)} on the coach across ${s.active_days} day(s) and ${s.chats} chat(s). The list is ordered by time: a child who opens a new chat for every question is not necessarily the most engaged, which is why chat counts stopped being the measure (call 2026-08-26).`}
                    >
                      <span className="font-semibold text-slate-700">{fmtMins(s.minutes_total)}</span>
                      <span className="block text-[11px] text-slate-400">
                        {s.active_days} day{s.active_days === 1 ? '' : 's'} · {s.chats} chats
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </MonitorLayout>
  );
};

// A donut gauge for a share/percentage learning-outcome metric.
const WhoBadge: React.FC<{ who: 'Students' | 'Facilitators' }> = ({ who }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
      who === 'Facilitators' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-600'
    }`}
  >
    {who}
  </span>
);

const OutcomeRing: React.FC<{
  pct: number | null;
  color: string;
  title: string;
  sub: string;
  who: 'Students' | 'Facilitators';
  hint?: string;
}> = ({ pct, color, title, sub, who, hint }) => {
  const R = 34;
  const C = 2 * Math.PI * R;
  const p = pct == null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="relative flex flex-col items-center rounded-2xl border border-[#E9E6F4] bg-white p-4 text-center">
      {hint && <InfoDot hint={hint} className="absolute right-2.5 top-2.5" />}
      <div className="mb-2"><WhoBadge who={who} /></div>
      <div className="relative h-[92px] w-[92px]">
        <svg viewBox="0 0 96 96" className="h-full w-full -rotate-90">
          <circle cx="48" cy="48" r={R} fill="none" stroke="#EDEBFA" strokeWidth="10" />
          <circle
            cx="48"
            cy="48"
            r={R}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${(p / 100) * C} ${C}`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-extrabold text-slate-900">{pct == null ? '—' : `${pct}%`}</span>
        </div>
      </div>
      <p className="mt-2.5 text-sm font-semibold leading-snug text-slate-800">{title}</p>
      <p className="mt-0.5 text-xs text-slate-400">{sub}</p>
    </div>
  );
};

// School hours, IST. Must match _SCHOOL_START_HOUR_IST / _SCHOOL_END_HOUR_IST in
// backend/src/admin_api/router.py — the backend decides which messages are
// after-school, this only draws the band. Athira's team read 9–4 on the
// 2026-08-19 call; VU still to confirm the bells and Saturday.
const SCHOOL_START_HOUR = 9;
const SCHOOL_END_HOUR = 16;
const fmtHour12 = (h: number): string => `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`;
const SCHOOL_HOURS_LABEL = `${fmtHour12(SCHOOL_START_HOUR)}–${fmtHour12(SCHOOL_END_HOUR)}`;

// Hourly bars: student messages by hour of day (IST), split school-hours (muted)
// vs after-school (violet). A shaded "school hours" band anchors the time so the
// after-school (evening) surge reads at a glance; hover any hour for exact counts.
const hourLabel = (h: number): string =>
  h === 0 ? '12am' : h === 12 ? '12pm' : `${h % 12}${h < 12 ? 'am' : 'pm'}`;
const hourLong = (h: number): string => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'am' : 'pm'}`;
const HourlyChart: React.FC<{ hours: ActivityByHour['hours']; afterPct: number | null }> = ({ hours, afterPct }) => {
  const W = 640, H = 224, padL = 28, padR = 10, padT = 16, padB = 48;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...hours.map((h) => h.school + h.after_school));
  const step = plotW / 24;
  const barW = step * 0.66;
  const baseline = padT + plotH;
  const yTop = (v: number) => padT + (1 - v / max) * plotH;
  const bandX0 = padL + SCHOOL_START_HOUR * step;
  const bandX1 = padL + SCHOOL_END_HOUR * step;
  const ticks = [0, 3, 6, 9, 12, 15, 18, 21];
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-600">
        <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: '#7C3AED' }} /> After school</span>
        <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: '#CBD5E1' }} /> During school hours</span>
        {afterPct != null && <span className="text-slate-400">· {afterPct}% of all messages sent after school</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        {/* school-hours band — the time anchor */}
        <rect x={bandX0} y={padT} width={bandX1 - bandX0} height={plotH} fill="#F2F1F9" />
        <text x={(bandX0 + bandX1) / 2} y={padT - 5} fontSize="9" fill="#94A3B8" textAnchor="middle">← school hours ({SCHOOL_HOURS_LABEL}) →</text>
        {[0, 0.5, 1].map((f, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={yTop(max * f)} y2={yTop(max * f)} stroke="#EDEBF7" strokeWidth="1" />
            <text x={2} y={yTop(max * f) + 3} fontSize="9" fill="#B7B3C9">{Math.round(max * f)}</text>
          </g>
        ))}
        <line x1={padL} x2={W - padR} y1={baseline} y2={baseline} stroke="#DEDCEC" strokeWidth="1" />
        {hours.map((h) => {
          const x = padL + h.hour * step + (step - barW) / 2;
          const total = h.school + h.after_school;
          const schoolH = (h.school / max) * plotH;
          const afterH = (h.after_school / max) * plotH;
          const schoolY = baseline - schoolH;
          const afterY = schoolY - afterH;
          return (
            <g key={h.hour}>
              <title>{`${hourLong(h.hour)} — ${total} message${total === 1 ? '' : 's'}${total ? ` · ${Math.round((100 * h.after_school) / total)}% after school` : ''}`}</title>
              <rect x={padL + h.hour * step} y={padT} width={step} height={plotH} fill="transparent" />
              {h.school > 0 && <rect x={x} y={schoolY} width={barW} height={schoolH} rx="1" fill="#CBD5E1" />}
              {h.after_school > 0 && <rect x={x} y={afterY} width={barW} height={afterH} rx="1" fill="#7C3AED" />}
            </g>
          );
        })}
        {ticks.map((h) => (
          <text key={h} x={padL + h * step + step / 2} y={baseline + 16} fontSize="10" fill="#6B6980" textAnchor="middle">
            {hourLabel(h)}
          </text>
        ))}
        <text x={padL + plotW / 2} y={H - 6} fontSize="9" fill="#9A96AD" textAnchor="middle">time of day (student's local time, IST)</text>
      </svg>
    </div>
  );
};

// Composition donut: active students per school (part-to-whole). Colours follow
// the school (fixed order), not rank, so the legend and arcs stay in sync.
const SCHOOL_COLORS = ['#7C3AED', '#2563EB', '#0891B2', '#059669', '#D97706', '#DB2777', '#6366F1', '#94A3B8'];
const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const a = ((deg - 90) * Math.PI) / 180; // 0° at 12 o'clock, clockwise
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

const SchoolDonut: React.FC<{ schools: ActivityBySchool['schools'] }> = ({ schools }) => {
  // Top 7, fold the rest into "Other" so the donut stays legible.
  const MAX = 7;
  const active = [...schools].filter((s) => s.active_students > 0);
  let slices = active;
  if (active.length > MAX) {
    const tail = active.slice(MAX - 1);
    slices = [
      ...active.slice(0, MAX - 1),
      {
        school: `Other (${tail.length})`,
        active_students: tail.reduce((a, s) => a + s.active_students, 0),
        chats: tail.reduce((a, s) => a + s.chats, 0),
        minutes_total: tail.reduce((a, s) => a + s.minutes_total, 0),
        // Re-derived from the folded totals, never averaged from averages.
        minutes_per_active_day: null as number | null,
      },
    ];
  }
  const total = slices.reduce((a, s) => a + s.active_students, 0);
  if (total === 0) return <p className="text-sm text-slate-400">No student activity in this period yet.</p>;

  const cx = 70, cy = 70, R = 52, SW = 16;
  let acc = 0;
  const arcs = slices.map((s, i) => {
    const frac = s.active_students / total;
    const a0 = acc * 360;
    acc += frac;
    const a1 = acc * 360;
    const color = SCHOOL_COLORS[i % SCHOOL_COLORS.length];
    const [x0, y0] = polar(cx, cy, R, a0);
    const [x1, y1] = polar(cx, cy, R, a1);
    const large = a1 - a0 > 180 ? 1 : 0;
    // A single 100% slice can't be drawn as an arc (start == end) — full ring.
    const full = frac >= 0.9999;
    return { full, color, d: `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}` };
  });

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row">
      <div className="relative h-[160px] w-[160px] flex-none">
        <svg viewBox="0 0 140 140" className="h-full w-full">
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="#F1EFFA" strokeWidth={SW} />
          {arcs.map((a, i) =>
            a.full ? (
              <circle key={i} cx={cx} cy={cy} r={R} fill="none" stroke={a.color} strokeWidth={SW} />
            ) : (
              <path key={i} d={a.d} fill="none" stroke={a.color} strokeWidth={SW} strokeLinecap="butt" />
            )
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-extrabold text-slate-900">{total}</span>
          <span className="text-[11px] font-medium text-slate-400">active students</span>
        </div>
      </div>
      <div className="w-full flex-1 space-y-1.5">
        {slices.map((s, i) => (
          <div key={s.school} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 flex-none rounded-sm" style={{ background: SCHOOL_COLORS[i % SCHOOL_COLORS.length] }} />
            <span className="min-w-0 flex-1 truncate font-semibold text-slate-700">{s.school}</span>
            <span
              className="flex-none cursor-help tabular-nums text-slate-400"
              title={`${s.chats} chat${s.chats === 1 ? '' : 's'} · ${fmtMins(s.minutes_total)} in total. Chat counts were dropped on the 2026-08-26 call; the figure shown is time on a day a child here actually uses the coach.`}
            >
              {s.minutes_per_active_day == null ? '—' : `${fmtMins(s.minutes_per_active_day)}/day`}
            </span>
            <span className="w-16 flex-none text-right font-bold tabular-nums text-slate-800">
              {s.active_students} · {Math.round((100 * s.active_students) / total)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Trend, ONE measure at a time (2026-08-20). Five lines in one box could not be
// read by anyone outside the team: the lines crossed constantly, and on a day
// when a single child chatted a % swung to 0 or 100, so the box was mostly
// spikes. Now you pick one measure and the line is its WEEKLY average — the
// shape you can actually read. The daily figures are not lost: the cursor reads
// out any single day. Labels are in plain words, no "CC", no "STEM sessions".
// The default measure is the % of children asking why / how / what-if: Athira's
// read on whether children are getting more curious is the shift in the KIND of
// question they ask (2026-08-19).
const OUTCOME_SERIES = [
  {
    key: 'exploratory_pct',
    label: 'Curious questions',
    sentence: 'of children asked a why, how or what-if question',
    color: '#C026D3',
    who: 'Students' as const,
  },
  {
    key: 'inside_module_pct',
    label: 'About the module',
    sentence: "of children asked about the week's module",
    color: '#0891B2',
    who: 'Students' as const,
  },
  {
    key: 'outside_module_pct',
    label: 'Their own topics',
    sentence: 'of children asked about something off the module',
    color: '#7C3AED',
    who: 'Students' as const,
  },
  {
    key: 'facilitators_pct',
    label: 'Facilitators using the coach',
    sentence: 'of facilitators used the coach on a normal day',
    color: '#059669',
    who: 'Facilitators' as const,
  },
  // The old "School visits held" line came off the visit schedule, which Athira
  // 2026-08-25 had us drop everywhere — so it is gone from the trend too.
] as const;

const seriesVal = (p: OutcomesTrend['points'][number], key: string) =>
  (p as unknown as Record<string, number | null>)[key];

const OutcomeTrendChart: React.FC<{ points: OutcomesTrend['points'] }> = ({ points }) => {
  const [seriesKey, setSeriesKey] = useState<string>(OUTCOME_SERIES[0].key);
  const [hover, setHover] = useState<number | null>(null);
  const sm = OUTCOME_SERIES.find((s) => s.key === seriesKey) ?? OUTCOME_SERIES[0];
  const W = 640, H = 200, padL = 28, padR = 10, padT = 12, padB = 22;
  const n = points.length;
  const x = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
  const y = (v: number) => padT + (1 - v / 100) * (H - padT - padB);

  // Weekly averages over the daily points the API returns (one consecutive run
  // of days, Monday-aligned weeks). A week with no figure at all breaks the line
  // rather than being drawn as a zero.
  const { weeks, weekOfDay } = useMemo(() => {
    if (!n) return { weeks: [] as { x: number; avg: number | null; start: string }[], weekOfDay: [] as number[] };
    const off = (new Date(points[0].date).getUTCDay() + 6) % 7;   // Mon = 0
    const buckets = new Map<number, { idx: number[]; vals: number[] }>();
    const of: number[] = [];
    points.forEach((p, i) => {
      const b = Math.floor((i + off) / 7);
      const e = buckets.get(b) ?? { idx: [], vals: [] };
      e.idx.push(i);
      const v = seriesVal(p, seriesKey);
      if (v != null) e.vals.push(v);
      buckets.set(b, e);
      of.push(b);
    });
    const keys = Array.from(buckets.keys()).sort((a, b) => a - b);
    const ws = keys.map((k) => {
      const e = buckets.get(k) as { idx: number[]; vals: number[] };
      return {
        x: e.idx.reduce((a, b) => a + b, 0) / e.idx.length,
        start: points[e.idx[0]].date,
        avg: e.vals.length ? Math.round(e.vals.reduce((a, b) => a + b, 0) / e.vals.length) : null,
      };
    });
    const pos = new Map(keys.map((k, i) => [k, i]));
    return { weeks: ws, weekOfDay: of.map((b) => pos.get(b) as number) };
  }, [points, seriesKey, n]);

  // Contiguous runs of weeks that have a figure — one line + one fill per run.
  const runs = useMemo(() => {
    const out: { x: number; v: number }[][] = [];
    weeks.forEach((w) => {
      if (w.avg == null) { if (out.length && out[out.length - 1].length) out.push([]); return; }
      if (!out.length) out.push([]);
      out[out.length - 1].push({ x: w.x, v: w.avg });
    });
    return out.filter((r) => r.length);
  }, [weeks]);
  const pt = (p: { x: number; v: number }) => `${x(p.x).toFixed(1)},${y(p.v).toFixed(1)}`;
  const linePath = runs.map((r) => `M${r.map(pt).join('L')}`).join(' ');
  const areaPath = runs
    .filter((r) => r.length > 1)
    .map((r) => `M${x(r[0].x).toFixed(1)},${y(0).toFixed(1)}L${r.map(pt).join('L')}L${x(r[r.length - 1].x).toFixed(1)},${y(0).toFixed(1)}Z`)
    .join(' ');

  // Headline: the latest week that has a figure, against the earliest one, so
  // the reader is told the direction instead of having to work it out.
  const withVal = weeks.filter((w) => w.avg != null);
  const latest = withVal.length ? withVal[withVal.length - 1] : null;
  const earliest = withVal.length > 1 ? withVal[0] : null;
  const delta = latest && earliest ? (latest.avg as number) - (earliest.avg as number) : null;

  const fmt = (s: string) => new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const fmtLong = (s: string) =>
    new Date(s).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const fmtMonth = (s: string) => new Date(s).toLocaleDateString(undefined, { month: 'long' });
  const ticks = Array.from(new Set([0, Math.floor((n - 1) / 2), n - 1])).filter((v) => v >= 0 && v < n);
  // Nearest day to the pointer — the whole plot is one hit area, so you never
  // have to land on the 2px line itself to read a value.
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    if (!box.width || n === 0) return;
    const px = ((e.clientX - box.left) / box.width) * W;
    let best = 0;
    for (let i = 1; i < n; i += 1) {
      if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
    }
    setHover(best);
  };
  const hp = hover == null ? null : points[hover];
  const hv = hp ? seriesVal(hp, seriesKey) : null;
  const hw = hover == null ? null : weeks[weekOfDay[hover]];
  return (
    <div>
      {/* Pick one measure. Only the picked one is drawn, so there is never a
          legend to decode or two lines crossing each other. */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {OUTCOME_SERIES.map((s) => {
          const on = s.key === seriesKey;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => { setSeriesKey(s.key); setHover(null); }}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                on ? 'text-white' : 'border border-[#E9E6F4] bg-white text-slate-500 hover:bg-slate-50'
              }`}
              style={on ? { background: s.color } : undefined}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      <div className="mb-0.5 flex flex-wrap items-end gap-2.5">
        <span className="text-3xl font-bold leading-none tracking-tight text-slate-900">
          {latest?.avg == null ? '—' : `${latest.avg}%`}
        </span>
        {delta != null && earliest && (
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              delta > 0
                ? 'bg-emerald-50 text-emerald-700'
                : delta < 0
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-100 text-slate-500'
            }`}
          >
            {delta > 0 ? 'up' : delta < 0 ? 'down' : 'same as'} {delta === 0 ? '' : 'from '}
            {earliest.avg}% in {fmtMonth(earliest.start)}
          </span>
        )}
        <WhoBadge who={sm.who} />
      </div>
      <p className="mb-2 text-xs font-medium text-slate-500">{sm.sentence}, last week</p>

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          {[0, 50, 100].map((v, i) => (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="#F1EFFA" strokeWidth="1" />
              <text x={2} y={y(v) + 3} fontSize="9" fill="#B7B3C9">{v}%</text>
            </g>
          ))}
          {hover != null && (
            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={H - padB} stroke="#D9D4EE" strokeWidth="1" />
          )}
          <path d={areaPath} fill={sm.color} fillOpacity="0.09" />
          <path d={linePath} fill="none" stroke={sm.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {weeks.map((w, i) => (
            w.avg == null ? null : (
              <circle key={i} cx={x(w.x)} cy={y(w.avg)} r="3" fill="#fff" stroke={sm.color} strokeWidth="2" />
            )
          ))}
          {hover != null && hv != null && (
            <circle cx={x(hover)} cy={y(hv)} r="4" fill={sm.color} stroke="#fff" strokeWidth="1.5" />
          )}
          {ticks.map((i) => (
            <text key={i} x={x(i)} y={H - 5} fontSize="9" fill="#9A96AD" textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
              {fmt(points[i].date)}
            </text>
          ))}
        </svg>
        {hp && (
          <div
            className="pointer-events-none absolute top-0 z-10 w-52 rounded-xl border border-[#E9E6F4] bg-white p-2.5 text-left shadow-lg"
            style={{
              left: `${(x(hover as number) / W) * 100}%`,
              transform: `translateX(${(hover as number) > n / 2 ? '-105%' : '5%'})`,
            }}
          >
            <p className="text-xs font-bold text-slate-800">{fmtLong(hp.date)}</p>
            <p className="mb-1.5 text-[11px] text-slate-400">
              {hp.active_students} student{hp.active_students === 1 ? '' : 's'} used the coach this day
            </p>
            <p className="flex items-center justify-between gap-2 text-[11px] text-slate-600">
              <span className="truncate">This day</span>
              <span className="flex-none font-bold text-slate-800">{hv == null ? 'no figure' : `${hv}%`}</span>
            </p>
            <p className="flex items-center justify-between gap-2 text-[11px] text-slate-600">
              <span className="truncate">That week (% of students)</span>
              <span className="flex-none font-bold text-slate-800">
                {hw?.avg == null ? 'no figure' : `${hw.avg}%`}
              </span>
            </p>
          </div>
        )}
      </div>
      <p className="mt-2 text-[11px] font-medium text-slate-400">
        Each point on the line is one week's average, so one quiet day cannot swing it to 0% or 100%. Move your cursor
        over the chart to read any single day.
      </p>
    </div>
  );
};

export default MonitorDashboard;
